import NoImage from 'assets/images/noimage.png'
import { P } from 'components'
import PMBSelect from 'components/PMBSelect'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, FormGroup, Modal, ModalBody, Spinner } from 'reactstrap'
import { useFetchFindoraItemsQuery } from '../sync-data/syncDataListApiSlice'
import {
  useGetProductProcessQuery,
  usePostAddProcessMutation,
} from './productMappingListApiSlice'

const ModalAddProcess = ({
  isOpen,
  toggle,
  onSuccess,
  dataExistingConfiguration,
}) => {
  const { enqueueSnackbar } = useSnackbar()

  const [postAddProcess, { isLoading }] = usePostAddProcessMutation()

  const { data } = useGetProductProcessQuery(
    {
      page: 0,
      size: 9999,
    },
    { refetchOnMountOrArgChange: true, skip: !isOpen },
  )

  const { data: findoraCommodityItems } = useFetchFindoraItemsQuery(
    {
      page: 0,
      size: 9999,
    },
    { refetchOnMountOrArgChange: true, skip: !isOpen },
  )

  // MAPPING PRODUCT PROCESS LIST
  const productProcessList = useMemo(() => {
    return data?.items?.map((item) => ({
      label: item?.product_process_name,
      value: item?.product_process_id,
    }))
  }, [data])

  // MAPPING COMMODITY LIST
  const findoraCommodityList = useMemo(() => {
    return findoraCommodityItems?.items?.map((item) => ({
      label: item?.nameItem,
      value: item?.id,
      image: item?.linkFoto || NoImage,
    }))
  }, [findoraCommodityItems])

  const inputRef = useRef(null)

  const [payload, setPayload] = useState({
    productProcess: null,
    itemCommodity: null,
  })

  const resetState = () => {
    setPayload({
      productProcess: null,
      itemCommodity: null,
    })
  }

  const handleSubmit = async () => {
    try {
      if (isFormInvalid) {
        enqueueSnackbar('Semua data harus diisi', {
          variant: 'error',
        })
        return
      }

      const payloadPost = {
        product_process_id: payload.productProcess?.value,
        itemId: payload.itemCommodity?.value,
      }

      const response = await postAddProcess(payloadPost)

      if (response?.data) {
        enqueueSnackbar('Proses berhasil ditambahkan', {
          variant: 'success',
        })
        toggle()
        resetState()
        onSuccess()
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Proses gagal ditambahkan', {
        variant: 'error',
      })
    }
  }

  const isFormInvalid = !payload?.productProcess || !payload?.itemCommodity

  // AUTO FOCUS
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100)
  }, [isOpen])

  // AUTO RESET STATE PAYLOAD
  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        resetState()
      }, 300)

      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  // AUTO SELECT IF ONLY ONE ITEM IN PRODUCT PROCESS LIST
  useEffect(() => {
    if (isOpen && productProcessList?.length === 1 && !payload.productProcess) {
      setPayload((prev) => ({ ...prev, productProcess: productProcessList[0] }))
    }
  }, [isOpen, productProcessList, payload.productProcess])

  // AUTO SELECT IF ONLY ONE ITEM IN COMMODITY LIST
  useEffect(() => {
    if (isOpen && findoraCommodityList?.length === 1 && !payload.itemCommodity) {
      setPayload((prev) => ({ ...prev, itemCommodity: findoraCommodityList[0] }))
    }
  }, [isOpen, findoraCommodityList, payload.itemCommodity])

  return (
    <>
      <Modal
        className="p-4"
        centered
        isOpen={isOpen}
        toggle={toggle}
        autoFocus={false}
        style={{ maxWidth: '600px' }}
        data-testid="product-mapping-modal-add-process"
      >
        <ModalBody>
          <div className="d-flex flex-column gap-1">
            <P className="text-sm pb-2" size="medium" color="#101828" bold>
              Tambah Proses
            </P>

            <div
              className="pt-2 pb-4 overflow-y-auto"
              style={{
                maxHeight: 550,
              }}
            >
              <FormGroup>
                <P className="text-sm pb-1" size="regular" color="#344054">
                  Nama Proses<span style={{ color: '#b42218' }}>*</span>
                </P>
                <PMBSelect
                  value={payload.productProcess}
                  options={productProcessList}
                  onChange={(e) =>
                    setPayload((prev) => ({ ...prev, productProcess: e }))
                  }
                  getOptionLabel={(e) => e.label}
                  name="productProcess"
                  style={{ width: '100%' }}
                  menuPosition="fixed"
                  menuPlacement="bottom"
                  menuPortalTarget={
                    typeof window !== 'undefined' ? document.body : null
                  }
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#d1d5db',
                      },
                      borderRadius: '8px',
                      padding: '2px',
                      minWidth: '100%',
                      maxWidth: '100%',
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e0f2ff',
                      borderRadius: '12px',
                      padding: '2px 6px',
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#0369a1',
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#0369a1',
                      '&:hover': {
                        backgroundColor: '#b3e5fc',
                        color: '#005f8e',
                      },
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#6b7280',
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      color: '#6b7280',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      maxHeight: 120,
                      overflowY: 'auto',
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </FormGroup>

              <FormGroup>
                <P className="text-sm pb-1" size="regular" color="#344054">
                  Barang<span style={{ color: '#b42218' }}>*</span>
                </P>
                <PMBSelect
                  value={payload.itemCommodity}
                  options={findoraCommodityList}
                  onChange={(e) =>
                    setPayload((prev) => ({ ...prev, itemCommodity: e }))
                  }
                  getOptionLabel={(e) => e.label}
                  name="itemCommodity"
                  style={{ width: '100%' }}
                  menuPosition="fixed"
                  menuPlacement="bottom"
                  menuPortalTarget={
                    typeof window !== 'undefined' ? document.body : null
                  }
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: '#d1d5db',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: '#d1d5db',
                      },
                      borderRadius: '8px',
                      padding: '2px',
                      minWidth: '100%',
                      maxWidth: '100%',
                    }),
                    multiValue: (provided) => ({
                      ...provided,
                      backgroundColor: '#e0f2ff',
                      borderRadius: '12px',
                      padding: '2px 6px',
                    }),
                    multiValueLabel: (provided) => ({
                      ...provided,
                      color: '#0369a1',
                    }),
                    multiValueRemove: (provided) => ({
                      ...provided,
                      color: '#0369a1',
                      '&:hover': {
                        backgroundColor: '#b3e5fc',
                        color: '#005f8e',
                      },
                    }),
                    dropdownIndicator: (provided) => ({
                      ...provided,
                      color: '#6b7280',
                    }),
                    clearIndicator: (provided) => ({
                      ...provided,
                      color: '#6b7280',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      maxHeight: 120,
                      overflowY: 'auto',
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                  }}
                />
              </FormGroup>
            </div>
          </div>

          <div className="d-flex gap-3 mt-3">
            <Button
              onClick={() => toggle()}
              className="btn-cancel"
              outline
              block
              data-testid="product-mapping-modal-add-process-cancel-button"
            >
              Batal
            </Button>
            <Button
              color="primary"
              block
              disabled={isLoading || isFormInvalid}
              onClick={handleSubmit}
              data-testid="product-mapping-modal-add-process-submit-button"
            >
              {isLoading && <Spinner size="sm" className="me-2" />}
              Simpan
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default ModalAddProcess
